import { Component, type ErrorInfo, type ReactNode } from "react";

import { colours } from "../styles/tokens";

/**
 * The app had no error boundary at all.
 *
 * React unmounts the whole tree when a render throws and nothing catches it, so
 * a fault in any one screen did not break that screen: it blanked the entire
 * document, with no message, no navigation and no way back except a manual
 * reload. That is what "the page is white" means, and it is why it looked like
 * the route was missing rather than broken.
 *
 * Two different failures land here and they need different answers.
 *
 * **A stale chunk.** Every screen past the landing page is a dynamic import
 * (see MajorRoutes), so the browser fetches its JavaScript when the route is
 * first visited. Filenames carry a content hash, so a deployment replaces them.
 * A tab opened before a deploy is still holding the previous index.html and
 * asks for a chunk that no longer exists; the import rejects and the app
 * blanks. Nothing is wrong with the code, and the fix is to fetch the current
 * index.html, so this reloads.
 *
 * The reload happens once, guarded by a sessionStorage timestamp. Without the
 * guard a genuinely missing chunk, an offline device or a proxy serving
 * index.html for a 404 would reload forever, which is worse than the blank page
 * it replaces.
 *
 * The guard used to be a flag cleared in `componentDidMount`, so that a deploy
 * later in the same session still got a reload. That clear ran on every page
 * load, before the lazy child had resolved, so it wiped the flag the previous
 * reload had just set and the loop it existed to prevent was never prevented. A
 * timestamp answers both questions without needing to be cleared at all.
 *
 * **Anything else.** A real bug. Reloading would only reproduce it, so this
 * renders a message and offers the two things that can actually help: going
 * home, or trying again after a deploy has landed.
 */

interface Props {
    children: ReactNode;
}

interface State {
    error: Error | null;
}

/** When this tab last reloaded itself for a failed chunk. */
const RELOAD_KEY = "hb:chunk-reloaded";

/**
 * How long a reload counts as "just happened".
 *
 * The guard has to answer two questions with one value: did I already reload
 * for THIS failure, and is this instead a fresh deploy later in the same
 * session that deserves its own reload. A flag cannot tell them apart, which is
 * why the flag had to be cleared, and clearing it was what broke it.
 *
 * A timestamp separates them. Anything inside this window is the same failure
 * surviving a reload, so the chunk is really gone and the message is the honest
 * answer. Anything older is a new deploy and gets its reload. Thirty seconds is
 * long enough to cover a slow fetch of index.html and the chunk on a bad
 * connection, and no deploy happens twice inside it.
 */
const RELOAD_WINDOW_MS = 30_000;

/**
 * Whether an error is a failed dynamic import.
 *
 * Matched on the message because browsers do not agree on a type or a code for
 * it: Chrome throws a TypeError reading "Failed to fetch dynamically imported
 * module", Firefox an "error loading dynamically imported module", and Safari a
 * plain "Importing a module script failed". Vite's own preload helper adds a
 * fourth wording. Matching loosely and reloading once is safe; matching
 * narrowly means the case this exists for slips through on one browser.
 */
function isChunkLoadError(error: Error): boolean {
    const text = `${error.name} ${error.message}`.toLowerCase();
    return (
        text.includes("dynamically imported module") ||
        text.includes("importing a module script failed") ||
        text.includes("failed to fetch dynamically") ||
        text.includes("error loading dynamically imported module") ||
        text.includes("chunkloaderror")
    );
}

class RouteErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Logged before any reload, so a stale-chunk loop leaves evidence in
        // the console of the tab it happened in.
        console.error("Route failed to render", error, info.componentStack);

        if (!isChunkLoadError(error)) return;

        try {
            // Already reloaded for this failure, so the chunk is really gone
            // rather than merely stale. Fall through to the message.
            const last = Number(sessionStorage.getItem(RELOAD_KEY));
            if (last && Date.now() - last < RELOAD_WINDOW_MS) return;
            sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
            // Private mode, or storage disabled. Without somewhere to record
            // the attempt there is no guard against looping, and a message
            // beats a reload loop.
            return;
        }

        window.location.reload();
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        return (
            <div
                role="alert"
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "1rem",
                    padding: "2rem",
                    textAlign: "center",
                }}
            >
                <h1 style={{ fontSize: "1.75rem", fontWeight: 700 }}>
                    This page could not be loaded
                </h1>
                <p style={{ maxWidth: "38rem", color: colours.bodyText }}>
                    Something went wrong while opening this page. The rest of
                    the site is still working.
                </p>
                <div style={{ display: "flex", gap: "1rem" }}>
                    <button
                        type="button"
                        className="btn primary-btn"
                        onClick={() => window.location.reload()}
                    >
                        Try again
                    </button>
                    <a className="btn secondary-btn" href="/">
                        Go to the home page
                    </a>
                </div>
            </div>
        );
    }
}

export default RouteErrorBoundary;
